import {
  getScrollbarWidth,
  hasScrollbar,
} from '@/app/components/overlays/utils/getScrollbarWidth';
import { isBrowser } from '@/app/utils/browser';

type TouchFn = (e: TouchEvent) => void;

let lockCount = 0;
let lastTouchY = 0;
let onTouchStart: TouchFn | null = null;
let onTouchMove: TouchFn | null = null;

const OPT_TOUCH_START = { passive: true, capture: true } as const;
const OPT_TOUCH_MOVE = { passive: false, capture: true } as const;

export const SCROLL_LOCK_WILL_LOCK_EVENT = 'overlay-scroll-lock-will-lock';

const findAllowed = (
  start: EventTarget | null,
  allowSel: string
): HTMLElement | null => {
  let n = start as HTMLElement | null;
  while (n && n !== document.body) {
    if (n.matches?.(allowSel)) return n;
    n = n.parentElement;
  }
  return null;
};

const canScrollY = (el: HTMLElement, dy: number): boolean => {
  if (!el) return false;
  if (dy > 0) return el.scrollTop + el.clientHeight < el.scrollHeight;
  if (dy < 0) return el.scrollTop > 0;
  return false;
};

const setScrollbarWidth = (): void => {
  const root = document.documentElement;
  const width = hasScrollbar() ? getScrollbarWidth() : 0;

  if (width > 0) {
    root.style.setProperty('--scrollbar-width', `${width}px`);
  } else {
    root.style.removeProperty('--scrollbar-width');
  }
};

const addTouchListeners = (allowSel: string): void => {
  onTouchStart = (e: TouchEvent) => {
    lastTouchY = e.touches?.[0]?.clientY ?? 0;
  };

  onTouchMove = (e: TouchEvent) => {
    const y = e.touches?.[0]?.clientY ?? lastTouchY;
    const dy = -(y - lastTouchY);
    const zone = findAllowed(e.target, allowSel);

    if (!zone || !canScrollY(zone, dy)) {
      if (e.cancelable) {
        e.preventDefault();
      }
    }
    lastTouchY = y;
  };

  document.addEventListener('touchstart', onTouchStart, OPT_TOUCH_START);
  document.addEventListener('touchmove', onTouchMove, OPT_TOUCH_MOVE);
};

const removeTouchListeners = (): void => {
  if (onTouchStart) {
    document.removeEventListener('touchstart', onTouchStart, OPT_TOUCH_START);
    onTouchStart = null;
  }

  if (onTouchMove) {
    document.removeEventListener('touchmove', onTouchMove, OPT_TOUCH_MOVE);
    onTouchMove = null;
  }
};

const resetState = (): void => {
  const root = document.documentElement;
  root.classList.remove('scroll-locked');
  root.style.removeProperty('--scrollbar-width');
  lastTouchY = 0;
};

export interface LockOpts {
  allow?: string;
}

/**
 * Locks the scroll by adding a `scroll-locked` class to `<html>`
 * and setting `--scrollbar-width` if scrollbar is visible.
 * Uses a reference counter to support nested locks (e.g. multiple modals).
 */
export const lockScroll = (opts?: LockOpts): void => {
  if (!isBrowser) return;

  const allowSel = opts?.allow ?? '.scroll-allow';
  lockCount += 1;

  if (lockCount > 1) return;

  try {
    setScrollbarWidth();
  } catch {
    // Silently handle scrollbar width calculation errors
  }

  window.dispatchEvent(new Event(SCROLL_LOCK_WILL_LOCK_EVENT));
  document.documentElement.classList.add('scroll-locked');
  addTouchListeners(allowSel);
};

/**
 * Unlocks the scroll when the lock count reaches zero.
 * Removes `scroll-locked` class and cleans up scrollbar width.
 */
export const unlockScroll = (): void => {
  if (!isBrowser || lockCount <= 0) return;

  lockCount -= 1;

  if (lockCount > 0) return;

  removeTouchListeners();
  resetState();
};
